import React from 'react';
import { useTranslation } from 'react-i18next';
import { ConfigProvider, Spin, Tooltip } from 'antd';
import { ArrowDownload16Regular } from '@fluentui/react-icons';

import { DMS_TOOLTIP_Z_INDEX, dmsAntdTheme } from '../../config/antdTheme';
import { ActionArtifact } from '../../types/actionTypes';
import { formatActionDateTime, formatActionFileSize } from '../../utils/actionPresentation';

interface ActionArtifactListProps {
  artifacts: ActionArtifact[];
  downloadingId?: number | null;
  onDownload: (artifact: ActionArtifact) => void;
  targetNameById?: Record<number, string>;
}

const ActionArtifactList: React.FC<ActionArtifactListProps> = ({
  artifacts,
  downloadingId,
  onDownload,
  targetNameById = {},
}) => {
  const { t, i18n } = useTranslation();
  if (artifacts.length === 0) return null;

  return (
    <ConfigProvider direction={i18n.dir()} theme={dmsAntdTheme}>
      <section className="action-artifacts" aria-label="Action files">
        <h3>Files</h3>
        {artifacts.map((artifact) => {
          const targetLabel = artifact.targetRunId
            ? targetNameById[artifact.targetRunId] || artifact.rtuName || (artifact.rtuId ? `RTU ${artifact.rtuId}` : `Target #${artifact.targetRunId}`)
            : 'Action upload';
          const downloadLabel = t('actionsModal.artifacts.download', { defaultValue: 'Download {{fileName}}', fileName: artifact.fileName });
          const isDownloading = downloadingId === artifact.id;
          return (
            <div className="action-artifacts__row" key={artifact.id}>
              <div>
                <strong>{artifact.fileName}</strong>
                <span>{artifact.artifactType || 'Action file'} · {formatActionFileSize(artifact.sizeBytes)} · {targetLabel}</span>
                <small>{formatActionDateTime(artifact.createdAt, 'Creation time not reported')}</small>
              </div>
              <Tooltip title={downloadLabel} zIndex={DMS_TOOLTIP_Z_INDEX}>
                <button
                  type="button"
                  className="action-artifacts__download"
                  onClick={() => onDownload(artifact)}
                  disabled={isDownloading}
                  aria-label={downloadLabel}
                >
                  {isDownloading ? <Spin size="small" /> : <ArrowDownload16Regular aria-hidden="true" />}
                </button>
              </Tooltip>
            </div>
          );
        })}
      </section>
    </ConfigProvider>
  );
};

export default ActionArtifactList;
